import { AxiosError } from 'axios'
import { type QueryFunctionContext } from 'react-query'

import { getOptionalServiceQueryKeyParams } from 'components/services/service-utils'
import { fetchQueryData } from 'hooks/use-gremlin-query'
import { getEndpointWithParams } from 'queries/queries-utils'
import { reliabilityTestsQueryKeys } from 'queries/reliability-tests/query-keys'

import type {
  PagedReliabilityTestRuns,
  ReliabilityTestRun
} from 'types/api/reliability-tests/runs'

export async function fetchReliabilityTestRunsById({
  queryKey: [endpoint, params]
}: QueryFunctionContext<
  ReturnType<typeof reliabilityTestsQueryKeys.getReliabilityTestRunsById>
>) {
  if (!endpoint) {
    throw new AxiosError('query did not provide a valid endpoint')
  }

  if (!params.serviceId) {
    throw new AxiosError('query did not provide a serviceId parameter')
  }

  const { dependencyId, failureFlagName, ...otherParams } = params
  const optionalParams = getOptionalServiceQueryKeyParams(
    dependencyId,
    failureFlagName
  )

  const endpointWithParams = getEndpointWithParams(endpoint, {
    ...optionalParams,
    ...otherParams
  })

  const response = await fetchQueryData<PagedReliabilityTestRuns>(
    endpointWithParams,
    {}
  )

  return response.items
}

/**
 * Given a set of ReliabilityTestRun data, return the run that matches the supplied
 * run number. If the run number does not match any of the known runs, return the most
 * recent run.
 */
export function selectReliabilityTestRunByNumber(
  data: ReliabilityTestRun[],
  runNumber: number
) {
  const latestRun = data[0]

  if (!latestRun) {
    return
  }

  if (runNumber === 0) {
    return latestRun
  }

  return data.find((r) => r.runNumber === runNumber) ?? latestRun
}
